import { useMemo, type ChangeEvent, type ReactNode } from 'react';
import type { CaseExternalModel, EnhancedOption } from 'models';
import _get from 'lodash/get';
import { RequestTypeCode, CaseExternalStatusCode } from 'utils/enums';

import {
  PageLoader,
  Error,
  BorderedBox,
  ButtonShape,
  Textarea,
  NoData,
  CheckIcon,
  ClearIcon,
} from 'components';
import InfoIcon from '@material-ui/icons/InfoRounded';

import bg from 'assets/img/premis_house.png';

import formatDate from 'shared/utils/formatDate';

import clsx from 'clsx';
import { useStyles } from './style';

type View = 'base' | 'complete' | 'cancel';
type FieldOption = EnhancedOption<ReactNode> & { type?: 'column' };

function getCompleteFormLabel(
  requestType: RequestTypeCode | null,
  isFromTask: boolean
) {
  if (isFromTask) {
    return 'Ilmoita ratkaisu:';
  }

  switch (requestType) {
    case RequestTypeCode.KeyOrder:
      return 'Lisää kuvaus tehtävän ratkaisusta / syy peruutukselle. Ratkaisu välitetään asiakkaalle portaalin kautta';
    default:
      return 'Lisää kuvaus tehtävän ratkaisusta. Ratkaisu välitetään asiakkaalle portaalin kautta.';
  }
}

function getCancelFormLabel(
  requestType: RequestTypeCode | null,
  isFromTask: boolean
) {
  if (isFromTask) {
    return 'Tehtävän peruuttamisen syy:';
  }

  switch (requestType) {
    case RequestTypeCode.KeyOrder:
      return 'Lisää kuvaus tehtävän ratkaisusta / syy peruutukselle. Ratkaisu välitetään asiakkaalle portaalin kautta';
    default:
      return 'Lisää tehtävän peruuttamisen syy.';
  }
}

interface Props {
  externalCase: CaseExternalModel | null;
  loading: boolean;
  error: boolean;
  resolve: boolean;
  reject: boolean;
  isValidationError: boolean;
  view: View;
  text: string;
  handleChangeView: (view: View) => void;
  handleChangeText: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  initValidation: () => void;
  handleRejectCase: () => void;
  handleCompleteCase: () => void;
}

const ExternalDetails = ({
  externalCase,
  loading,
  error,
  resolve,
  reject,
  isValidationError,
  view,
  text,
  handleChangeView,
  handleChangeText,
  initValidation,
  handleRejectCase,
  handleCompleteCase,
}: Props) => {
  const cssProps = { backgroundImage: `url(${bg})` };
  const classes = useStyles(cssProps);

  const options = useMemo<FieldOption[]>(() => {
    let caseLeveList: (string | null)[] = [];

    if (externalCase?.CaseLevel1) {
      caseLeveList.push(externalCase.CaseLevel1.Label);
    }

    if (externalCase?.CaseLevel2) {
      caseLeveList.push(externalCase.CaseLevel2.Label);
    }

    if (externalCase?.CaseLevel3) {
      caseLeveList.push(externalCase.CaseLevel3.Label);
    }

    const caseFields: FieldOption[] = externalCase?.IsFromTask
      ? [
          {
            Label: 'Taloyhtiön nimi',
            Value: _get(externalCase, 'CustomerRef.Name'),
          },
          {
            Label: 'Tila',
            Value: _get(externalCase, 'SpaceRef.Name'),
          },
          {
            Label: 'Taloyhtiön osoite',
            Value: _get(externalCase, 'BuildingAddress'),
          },
          {
            Label: 'Yhteyshenkilö',
            Value: _get(externalCase, 'ContactPersonRef.Name'),
          },
          {
            Label: 'Matkapuhelinnumero',
            Value: _get(externalCase, 'ContactPersonMobilePhone'),
          },
          {
            Label: 'Sähköposti',
            Value: _get(externalCase, 'ContactPersonEmail'),
          },
          {
            Label: 'Palvelupyyntö',
            Value: _get(externalCase, 'RequestType.Label'),
          },
          {
            Label: 'Luontipäivämäärä',
            Value: externalCase?.CreatedOn
              ? formatDate(new Date(externalCase?.CreatedOn))
              : null,
          },
          {
            Label: 'Yleisavain sallittu',
            Value: externalCase.MasterKeyAllowed ? (
              <CheckIcon />
            ) : (
              <ClearIcon />
            ),
          },
          {
            Label: 'Haluan, että huoltoaika sovitaan kanssani',
            Value: externalCase.RepairFormNeedToBeAgreed ? (
              <CheckIcon />
            ) : (
              <ClearIcon />
            ),
          },
          {
            Label: 'Tilassa/huoneistossa on turvalukko',
            Value: externalCase.AppartmentHasAdditionalLock ? (
              <CheckIcon />
            ) : (
              <ClearIcon />
            ),
          },
          {
            Label: 'Asiakkaan kuvaus',
            Value: externalCase.CustomerDescription,
          },
        ]
      : [
          {
            Label: 'Taloyhtiö',
            Value: _get(externalCase, 'CustomerRef.Name'),
          },
          {
            Label: 'Tila',
            Value: _get(externalCase, 'SpaceRef.Name'),
          },
          {
            Label: 'Yhteyshenkilö',
            Value: _get(externalCase, 'ContactPersonRef.Name') ?? null,
          },
          {
            Label: 'Palvelupyyntö',
            Value: caseLeveList.join(', '),
          },
          {
            Label: 'Asiakkaan kuvaus',
            Value: externalCase?.CustomerDescription || '',
            type: 'column',
          },
        ];

    const keyOrderFields: FieldOption[] = [
      {
        Label: 'Taloyhtiön nimi',
        Value: externalCase?.CustomerRef?.Name,
      },
      {
        Label: 'Tila',
        Value: externalCase?.SpaceRef?.Name,
      },
      {
        Label: 'Tilaaja',
        Value: externalCase?.ContactPersonRef?.Name,
      },
      {
        Label: 'Luontipäivämäärä',
        Value: externalCase?.CreatedOn
          ? formatDate(new Date(externalCase?.CreatedOn))
          : null,
      },
      {
        Label: 'Avaintilaustyyppi',
        Value: externalCase?.KeyOrderType?.Label,
      },
      {
        Label: 'Avainten lukumäärä',
        Value: externalCase?.AmountOfKeys,
      },
      {
        Label: 'Avaimen tyyppi',
        Value: externalCase?.TypeOfTheKey,
      },
      {
        Label: 'Avaimen numero',
        Value: externalCase?.KeyNumber,
      },
      {
        Label: 'Lisätiedot',
        Value: externalCase?.AdditionalInformation,
      },
    ];

    const spaceRenovationRequestFields: FieldOption[] = externalCase?.IsFromTask
      ? [
          {
            Label: 'Taloyhtiön nimi',
            Value: externalCase?.CustomerRef?.Name,
          },
          {
            Label: 'Tila',
            Value: externalCase?.SpaceRef?.Name,
          },
          {
            Label: 'Osoite',
            Value: `${externalCase?.BuildingAddress ?? ''}, ${
              externalCase?.BuildingPostalArea ?? ''
            }, ${externalCase?.BuildingPostalCode ?? ''}`,
          },
          {
            Label: 'Yhteyshenkilö',
            Value: externalCase?.ContactPersonRef?.Name,
          },
          {
            Label: 'Matkapuhelinnumero',
            Value: externalCase?.ContactPersonMobilePhone,
          },
          {
            Label: 'Sähköposti',
            Value: externalCase?.ContactPersonEmail,
          },
          {
            Label: 'Suunniteltu muutostyö',
            Value: externalCase?.RenovationTypes,
          },
          {
            Label: 'Projektin status',
            Value: externalCase?.CaseStatus?.Label,
          },
          {
            Label: 'Arvioitu ajoitus',
            Value: externalCase?.EstimatedTiming,
          },
          {
            Label: 'Kuvaus',
            Value: (
              <span>
                {externalCase?.AdditionalInformation && (
                  <span style={{ display: 'block' }}>
                    {externalCase.AdditionalInformation}
                  </span>
                )}
                {externalCase?.DescriptionOfTerms && (
                  <span style={{ display: 'block' }}>
                    {externalCase.DescriptionOfTerms}
                  </span>
                )}
              </span>
            ),
            type: 'column',
          },
        ]
      : [];

    return externalCase?.RequestType?.Value === RequestTypeCode.Case
      ? caseFields
      : externalCase?.RequestType?.Value === RequestTypeCode.KeyOrder
      ? keyOrderFields
      : externalCase?.RequestType?.Value ===
        RequestTypeCode.SpaceRenovationRequest
      ? spaceRenovationRequestFields
      : [];
  }, [externalCase]);

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.container}>
          <PageLoader />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.root}>
        <div className={classes.container}>
          <Error />
        </div>
      </div>
    );
  }

  if (!externalCase) {
    return (
      <div className={classes.root}>
        <div className={classes.container}>
          <NoData />
        </div>
      </div>
    );
  }

  const renderButtons = () => {
    return (
      <div className={classes.buttonsWrapper}>
        <div className={classes.btnWrapper}>
          <ButtonShape
            className={classes.completeBtn}
            onClick={() => handleChangeView('complete')}
          >
            ILMOITA SUORITETUKSI
          </ButtonShape>
        </div>
        <div className={classes.btnWrapper}>
          <ButtonShape
            className={classes.rejectBtn}
            onClick={() => handleChangeView('cancel')}
          >
            PERUUTA TEHTÄVÄ
          </ButtonShape>
        </div>
      </div>
    );
  };

  const renderCancelView = () => {
    const label = getCancelFormLabel(
      externalCase.RequestType?.Value ?? null,
      externalCase.IsFromTask
    );

    return (
      <div className={classes.viewWrapper}>
        <span className={classes.formLabel}>{label}</span>
        <Textarea
          value={text}
          onChange={handleChangeText}
          onClick={initValidation}
          className={clsx(classes.textarea, {
            [classes.textareaError]: isValidationError,
          })}
        />
        <div className={classes.buttonsWrapper}>
          <div className={classes.btnWrapper}>
            <ButtonShape
              className={classes.rejectBtn}
              onClick={handleRejectCase}
            >
              PERUUTA TEHTÄVÄ
            </ButtonShape>
          </div>
          <div className={classes.btnWrapper}>
            <ButtonShape
              className={classes.completeBtn}
              onClick={() => handleChangeView('complete')}
            >
              ILMOITA SUORITETUKSI
            </ButtonShape>
          </div>
        </div>
      </div>
    );
  };

  const renderCompleteView = () => {
    const label = getCompleteFormLabel(
      externalCase.RequestType?.Value ?? null,
      externalCase.IsFromTask
    );
    return (
      <div className={classes.viewWrapper}>
        <span className={classes.formLabel}>{label}</span>
        <Textarea
          value={text}
          onChange={handleChangeText}
          onClick={initValidation}
          className={clsx(classes.textarea, {
            [classes.textareaError]: isValidationError,
          })}
        />
        <div className={classes.buttonsWrapper}>
          <div className={classes.btnWrapper}>
            <ButtonShape
              className={classes.completeBtn}
              onClick={handleCompleteCase}
            >
              ILMOITA SUORITETUKSI
            </ButtonShape>
          </div>
          <div className={classes.btnWrapper}>
            <ButtonShape
              className={classes.rejectBtn}
              onClick={() => handleChangeView('cancel')}
            >
              PERUUTA TEHTÄVÄ
            </ButtonShape>
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    const getTaskHeader = () => {
      switch (externalCase.RequestType?.Value) {
        case RequestTypeCode.Case:
          return `${externalCase.RequestType.Label}: ${externalCase.Number}, ${externalCase.CustomerRef?.Name}`;
        case RequestTypeCode.KeyOrder:
          return `${externalCase.RequestType.Label} ${externalCase.Number}`;
        case RequestTypeCode.SpaceRenovationRequest:
          return `Huoneiston muutostyöilmoitus/${externalCase.Number}, ${
            externalCase.CustomerRef?.Name ?? ''
          }`;
        default:
          return `${externalCase.RequestType?.Label} ${externalCase.Number}`;
      }
    };

    const getBaseHeader = () => {
      switch (externalCase.RequestType?.Value) {
        case RequestTypeCode.Case:
        case RequestTypeCode.KeyOrder:
        case RequestTypeCode.PTS:
          return `${externalCase.RequestType?.Label} ${externalCase.Number}`;
        case RequestTypeCode.SpaceRenovationRequest:
          return `Huoneiston muutostyöilmoitus ${externalCase.Number}`;
        default:
          return null;
      }
    };
    return (
      <div className={classes.form}>
        <h3 className={classes.formTitle}>
          {externalCase.IsFromTask ? getTaskHeader() : getBaseHeader()}
        </h3>
        <div className={classes.valuesWrapper}>
          <ul className={classes.ul}>
            {options.map((option) => (
              <li
                className={clsx(classes.li, {
                  [classes.col]: option.type === 'column',
                })}
                key={option.Label}
              >
                <span
                  className={clsx(classes.label, {
                    [classes.colLabel]: option.type === 'column',
                  })}
                >
                  {option.Label}
                </span>
                <span className={classes.value}>{option.Value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderMainScreen = () => {
    switch (view) {
      case 'base':
        return renderButtons();
      case 'complete':
        return renderCompleteView();
      case 'cancel':
        return renderCancelView();
      default:
        return null;
    }
  };

  const renderContent = () => {
    const { Status } = externalCase;

    function r(status: CaseExternalStatusCode | null) {
      switch (status) {
        case CaseExternalStatusCode.Active:
          return renderMainScreen();
        case CaseExternalStatusCode.Expired:
          const label =
            externalCase?.IsFromTask ||
            externalCase?.RequestType?.Value === RequestTypeCode.KeyOrder
              ? 'Linkki on vanhentunut. Ota yhteyttä isännöitsijään ratkaistaksesi tehtävän'
              : 'Tämä linkki on vanhentunut. Teille on lähetetty uusi sähköposti, jossa on uusi toimiva linkki.';
          return <p>{label}</p>;
        case CaseExternalStatusCode.Inactive:
        case CaseExternalStatusCode.NoTasks:
          return <p>Linkki ei ole käytössä.</p>;
        case CaseExternalStatusCode.Completed:
        case CaseExternalStatusCode.Canceled:
          let statusText =
            Status === CaseExternalStatusCode.Completed
              ? 'suoritettu'
              : 'peruutettu';
          let message = externalCase?.IsFromTask
            ? externalCase.RequestType?.Value === RequestTypeCode.Case
              ? `Tämä palvelupyyntö ei ole enää ajankohtainen. Palvelupyyntö on ${statusText}`
              : externalCase.RequestType?.Value === RequestTypeCode.KeyOrder
              ? `Tämä avaintilaus ei ole enää ajankohtainen. Avaintilaus on ${statusText}`
              : externalCase.RequestType?.Value ===
                RequestTypeCode.SpaceRenovationRequest
              ? `Tämä muutostyö ei ole enää ajankohtainen. Muutostyö on ${statusText}`
              : `Tämä palvelupyyntö ei ole enää ajankohtainen. Palvelupyyntö on ${statusText}`
            : `Tämä palvelupyyntö ei ole enää ajankohtainen. Palvelupyyntö on ${statusText}`;

          return (
            <p>
              <InfoIcon className={classes.infoIcon} />
              <span>{message}</span>
            </p>
          );
        default:
          return null;
      }
    }

    return <div>{r(Status)}</div>;
  };

  const showContent = !reject && !resolve;

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <BorderedBox>
          {renderForm()}
          {resolve ? (
            <p>{`Palvelupyyntö ${_get(
              externalCase,
              'Number'
            )} on suoritettu.`}</p>
          ) : null}
          {reject ? (
            <p>{`Palvelupyyntö ${_get(
              externalCase,
              'Number'
            )} on peruutettu.`}</p>
          ) : null}
          {showContent ? renderContent() : null}
        </BorderedBox>
      </div>
    </div>
  );
};

export default ExternalDetails;
